import { useCallback } from 'react';
import { Trans } from '@lingui/macro';
import { Button, Drawer, LoadingOverlay } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';

import { EOrderStatus, TOrder, TOrderTicket } from '../../../orders/core/order';
import { RefundFeeExpress } from '../../../shared/refund-fee-express';
import { TTrip } from '../../../trips/trip';
import { useTicketRefund } from './use-ticket-refund';

type RefundTicketButton = {
  order: TOrder;
  ticket?: TOrderTicket;
  trip?: TTrip;
  onOrderUpdate: (order: TOrder) => void;
};

export const RefundTicketSection = observer(
  ({ order, ticket, trip, onOrderUpdate }: RefundTicketButton) => {
    const [opened, { open, close }] = useDisclosure(false);
    const handleTicketUpdate = useCallback(
      (order: TOrder) => {
        onOrderUpdate(order);
        close();
      },
      [close, onOrderUpdate]
    );

    const { loading, cancelOrder, actionTitle, description, isVisible } =
      useTicketRefund({
        order,
        ticket,
        onOrderUpdate: handleTicketUpdate,
      });

    if (!isVisible()) return null;
    if (!order) return null;

    const isOrderSuccess = order.status === EOrderStatus.success;

    return (
      <>
        <Drawer
          opened={opened}
          position="bottom"
          onClose={close}
          size="100%"
          title={<Trans>Отмена заказа</Trans>}
          closeOnClickOutside={false}
          closeButtonProps={{ disabled: loading }}
        >
          <div
            style={{ maxWidth: 560, margin: '0 auto', position: 'relative' }}
          >
            <LoadingOverlay visible={loading} />
            {description}

            {isOrderSuccess && (
              <div style={{ marginTop: 8 }}>
                <RefundFeeExpress routeNumber={trip?.route.number} />
              </div>
            )}

            <Button size="md" mt="lg" fullWidth onClick={cancelOrder}>
              {actionTitle}
            </Button>
            <Button
              size="md"
              variant="outline"
              mt="sm"
              fullWidth
              onClick={close}
            >
              <Trans>Отмена</Trans>
            </Button>
          </div>
        </Drawer>

        <Button
          size="md"
          variant="outline"
          color="red"
          fullWidth
          onClick={open}
        >
          {actionTitle}
        </Button>
      </>
    );
  }
);
